import { useLayoutEffect, useRef } from 'react';

// The horizontal scroll container prevents CSS sticky from following page scroll.
// Move the real table header vertically so column widths and horizontal scrolling
// remain shared with the table body.
const useStickyPriceTableHeader = (active, toolbarRef) => {
  const headerRef = useRef(null);

  useLayoutEffect(() => {
    const header = headerRef.current;
    const table = header?.closest('table');
    if (!active || !table) return undefined;

    let frame = null;
    let offset = 0;
    const update = () => {
      frame = null;
      const headerRect = header.getBoundingClientRect();
      const naturalTop = headerRect.top - offset;
      const toolbarBottom = Math.max(0, toolbarRef?.current?.getBoundingClientRect().bottom || 0);
      const maxOffset = Math.max(0, table.getBoundingClientRect().bottom - naturalTop - headerRect.height);
      const nextOffset = Math.min(maxOffset, Math.max(0, toolbarBottom - naturalTop));
      if (nextOffset === offset) return;
      offset = nextOffset;
      header.style.transform = `translateY(${offset}px)`;
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(table);
    observer?.observe(header);
    if (toolbarRef?.current) {
      observer?.observe(toolbarRef.current);
      // Notices between the toolbar and table can also move the table.
      if (toolbarRef.current.parentElement) observer?.observe(toolbarRef.current.parentElement);
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();

    return () => {
      observer?.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      header.style.removeProperty('transform');
    };
  }, [active, toolbarRef]);

  return headerRef;
};

export default useStickyPriceTableHeader;
